import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import BrandWordmark from '../common/BrandWordmark';
import {
  Home,
  FolderOpen,
  CheckSquare,
  MessageCircle,
  CreditCard,
  DollarSign,
  User,
  Settings,
  Users,
  Shield,
  X,
  Building2,
  Activity,
  UserCheck,
  BarChart3,
  Target,
} from 'lucide-react';

interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onToggle }) => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const location = useLocation();

  const getMenuItems = () => {
    const baseItems = [
      {
        name: 'Dashboard',
        href:
          user?.role === 'client'
            ? '/dashboard/client'
            : user?.role === 'developer'
            ? '/dashboard/developer'
            : '/dashboard/admin',
        icon: Home,
      },
      { name: 'Projects', href: '/dashboard/projects', icon: FolderOpen },
      { name: 'Chat', href: '/dashboard/chat', icon: MessageCircle },
    ];

    if (user?.role === 'client') {
      baseItems.push(
        {
          name: 'Payment Center',
          href: '/dashboard/payments',
          icon: CreditCard,
        },
       
  
      );
    }

    if (user?.role === 'developer') {
      baseItems.push(
        {
          name: 'Earnings',
          href: '/dashboard/earnings',
          icon: DollarSign,
        },
        { name: 'Tasks', href: '/dashboard/tasks', icon: CheckSquare }
      );
    }

    if (user?.role === 'admin') {
      baseItems.push(
        {
          name: 'Payment Requests',
          href: '/dashboard/admin/payment-requests',
          icon: CreditCard,
        },
         {
          name: 'Payments',
          href: '/dashboard/admin/payments',
          icon: DollarSign,
        },
        {
          name: 'Create Task',
          href: '/dashboard/create-task',
          icon: CheckSquare,
        },
        {
          name: 'Tasks',
          href: '/dashboard/tasks',
          icon: CheckSquare,
        },
        {
          name: 'User Management',
          href: '/dashboard/admin/users',
          icon: Users,
        },
        {
          name: 'Developer Approvals',
          href: '/dashboard/admin/developer-approvals',
          icon: UserCheck,
        },
        {
          name: 'Site Visitors',
          href: '/dashboard/admin/analytics',
          icon: BarChart3,
        },
        
        {
          name: 'System Settings',
          href: '/dashboard/admin/settings',
          icon: Shield,
        }
      );
    }

    baseItems.push(
      { name: 'Profile', href: '/dashboard/profile', icon: User },
      { name: 'Settings', href: '/dashboard/settings', icon: Settings }
    );

    return baseItems;
  };

  const menuItems = getMenuItems();

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 z-20 bg-black bg-opacity-50 lg:hidden"
          onClick={onToggle}
        />
      )}

      <div
        className={`
          fixed inset-y-0 left-0 z-30 w-64 transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
          ${theme === 'dark' ? 'bg-gray-800 shadow-lg' : 'bg-white shadow-lg'}
        `}
      >
        <div
          className={`flex items-center justify-between h-16 px-6 border-b ${
            theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
          }`}
        >
          <Link to='/' className="group">
         <div className="flex items-center justify-center">
          
              {theme === "dark" ? (
                <img
                  src="/logo1.png"
                  alt="AetherFlow Logo"
                  className="w-14 h-14 mr-2"
                />
              ) : (
                <img
                  src="/logo0.png"
                  alt="AetherFlow Logo"
                  className="w-14 h-14 mr-2"
                />
              )}
              <BrandWordmark theme={theme} variant="sidebar" />
            </div>
          </Link>
         
          <button
            onClick={onToggle}
            className={`lg:hidden p-2 rounded-md ${
              theme === 'dark'
                ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
            }`}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="mt-8 px-4">
          <ul className="space-y-2">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive =
                location.pathname === item.href ||
                (item.href !== '/dashboard' &&
                  location.pathname.startsWith(item.href));

              return (
                <li key={item.name}>
                  <Link
                    to={item.href}
                    className={`
                      flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors duration-200
                      ${
                        isActive
                          ? theme === 'dark'
                            ? 'bg-gray-700 text-blue-400 border-r-2 border-blue-400'
                            : 'bg-blue-50 text-blue-700 border-r-2 border-blue-600'
                          : theme === 'dark'
                          ? 'text-gray-300 hover:bg-gray-700 hover:text-white'
                          : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                      }
                    `}
                    onClick={() => {
                      if (window.innerWidth < 1024) onToggle();
                    }}
                  >
                    <Icon className="w-5 h-5 mr-3" />
                    {item.name}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        <div
          className={`absolute bottom-0 left-0 right-0 p-4 border-t ${
            theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
          }`}
        >
          <div className="flex items-center space-x-3">
            <div
              className={`w-10 h-10 rounded-full flex items-center justify-center ${
                theme === 'dark'
                  ? 'bg-gradient-to-r from-blue-600 to-purple-700'
                  : 'bg-gradient-to-r from-blue-500 to-purple-600'
              }`}
            >
              <span className="text-white font-semibold text-sm">
                {user?.firstName?.[0]}
                {user?.lastName?.[0]}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p
                className={`text-sm font-medium truncate ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}
              >
                {user?.firstName} {user?.lastName}
              </p>
              <p
                className={`text-xs capitalize ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                }`}
              >
                {user?.role}
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
